


































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import basketService from './basket.service';
import MinusCancel from './MinusCancel.vue';
import PlusAccept from './PlusAccept.vue';
import select from '@/core/form/select.service';
import Temporary from '@/core/ui-components/Temporary.vue';
import { debounce } from 'lodash';
import { PageName } from '@/router/routes/constants';

@Component({
    components: { PlusAccept, MinusCancel, Temporary }
})
export default class AddToBasket extends Vue {
    private editMode = false;
    private initialValue: string = '1';
    private justPlacedInBasket: boolean = false;
    private trackingCurrentDelta = 0;
    private debouncedChangeTracking = debounce(this.doChangeTracking, 1000);

    @Prop({ required: true, type: String })
    private productWid!: string;

    @Prop({ required: false, type: String })
    private lineItemId!: string | undefined;

    @Prop(Number)
    private bidId!: number | undefined;

    @Prop(String)
    private quoteId!: string | undefined;

    @Prop({ type: Boolean, default: false })
    private allowDelete!: boolean;

    @Prop({ type: Boolean, default: false })
    private disabled!: boolean;

    @Prop({ type: Boolean, default: false })
    private updateLineItemPrices!: boolean;

    @Prop({ type: Boolean, default: false })
    private updateDeliveryPrice!: boolean;

    @Prop({ type: Number, default: 99999 })
    private maxValue!: number;

    @Prop({ type: String, default: 'tile', validator: (value: string) => ['tile', 'tile-basket', 'tile-condensed', 'detailed'].includes(value) })
    private styleMode!: 'tile' | 'tile-basket' | 'tile-condensed' | 'detailed';

    private get mobileSlots() {
        return this.styleMode === 'tile' ? 'max-md' : 'md';
    }

    private get slots() {
        return this.styleMode === 'tile' ? 'min-md' : 'xs,sm,lg,xl';
    }

    private get minusDisabled(): boolean {
        return this.disabled || (!this.allowDelete && this.quantity <= 1 && !this.editMode);
    }

    private get plusDisabled(): boolean {
        return this.disabled;
    }

    private get quantity(): number {
        // Normally get value from store, but keep local changes while in editmode
        if (!this.editMode) {
            if (this.lineItemId) {
                return basketService.getQuantity(this.lineItemId, this.bidId, this.quoteId);
            } else {
                return basketService.getQuantityByWid(this.productWid, this.bidId, this.quoteId);
            }
        } else {
            return parseInt(this.$refs.inputElem.value, 10);
        }
    }

    private get tileMode() : boolean {
        return this.styleMode === 'tile';
    }

    private get tileBasketMode() : boolean {
        return this.styleMode === 'tile-basket';
    }

    private get tileCondensedMode() : boolean {
        return this.styleMode === 'tile-condensed';
    }

    private get detailedMode() : boolean {
        return this.styleMode === 'detailed';
    }

    private inputFocus() {
        this.editMode = true;
        select(this.$refs.inputElem);
    }

    private inputEnterEscKeyUp(event) {
        if (event.key === 'Enter') {
            this.setQuantity();
        }
        this.$refs.inputElem.blur();
        this.done();
    }

    private done() {
        this.editMode = false;
    }

    private inputBlur() {
        // Need to delay until a possible click on accept/cancel arrives. This comes after blur from inputfield...
        setTimeout(() => {
            if (this.editMode) {
                // All blur events exept cancel will set quantity
                this.setQuantity();
                this.done();
            }
        }, 250);
    }

    private isAllowedValue(val: number): boolean {
        return val >= (this.allowDelete ? 0 : 1) && !this.wouldBeOverMax(val);
    }

    private wouldBeOverMax(val: number): boolean {
        return val > this.maxValue;
    }

    private get isAtMax() {
        return this.quantity === this.maxValue;
    }

    private setQuantity() {
        let value = parseInt(this.$refs.inputElem.value, 10);
        if (!isNaN(value)) {
            if (value > this.maxValue) {
                value = this.maxValue;
            }

            if (!isNaN(value) && this.isAllowedValue(value)) {
                if (this.lineItemId) {
                    basketService.setQuantity(this.lineItemId, this.bidId, this.quoteId, value, this.updateLineItemPrices, this.updateDeliveryPrice);
                } else {
                    basketService.setQuantityByWid(this.productWid, this.bidId, this.quoteId, value, this.updateLineItemPrices, this.updateDeliveryPrice);
                }
                this.emitQuantityChange(value - this.quantity);
            }
        }

        this.done();
    }

    private deltaAdjustQuantity(delta: number) {
        const eventualValue = this.quantity + delta;
        if (!this.isAllowedValue(eventualValue)) {
            return;
        }

        if (this.lineItemId) {
            basketService.deltaUpdateQuantity(this.lineItemId, this.bidId, this.quoteId, delta, this.updateLineItemPrices, this.updateDeliveryPrice);
        } else {
            basketService.deltaUpdateQuantityByWid(this.productWid, this.bidId, this.quoteId, delta, this.updateLineItemPrices, this.updateDeliveryPrice);
        }
        this.emitQuantityChange(delta);
    }

    private addProduct() {
        let value = parseInt(this.initialValue, 10);
        if (value > this.maxValue) {
            value = this.maxValue;
        }

        basketService.addProduct(this.productWid, this.bidId, this.quoteId, value, this.updateLineItemPrices, this.updateDeliveryPrice);
        this.emitQuantityChange(value);
    }

    private emitQuantityChange(deltaQuantity: number) {
        this.trackingCurrentDelta += deltaQuantity;
        this.debouncedChangeTracking();
    }

    private doChangeTracking() {
        if (this.trackingCurrentDelta === 0) return;
        const quantityByWid = basketService.getQuantityByWid(this.productWid, this.bidId, this.quoteId);
        this.$emit('change-quantity', this.trackingCurrentDelta, quantityByWid);
        this.trackingCurrentDelta = 0;
    }

    private get showQuantityUpdateControls(): boolean {
        const isProductInBasket = basketService.isProductInBasket(this.productWid, this.bidId, this.quoteId);
        const isProductDuplicated = basketService.isProductDuplicated(this.productWid, this.bidId, this.quoteId);
        return (isProductInBasket && !isProductDuplicated) || this.lineItemId !== undefined;
    }

    private get showEditInBasketControls(): boolean {
        const isProductInBasket = basketService.isProductInBasket(this.productWid, this.bidId, this.quoteId);
        const isProductDuplicated = basketService.isProductDuplicated(this.productWid, this.bidId, this.quoteId);
        return isProductInBasket && isProductDuplicated && this.lineItemId === undefined;
    }

    @Watch('isProductInBasket')
    onProductBasketStatusChange(isInBasket: boolean) {
        if (isInBasket) {
            this.justPlacedInBasket = true;
        } else {
            this.justPlacedInBasket = false;
        }
    }

    private gotoBasket() {
        this.$router.push({ name: PageName.BASKET });
    }

    public $refs!: {
        // Just to have type...
        inputElem: HTMLInputElement;
        adjustWrapper: HTMLDivElement;
    };
}

